// LA INVESTIGACION DE CAUSALIDAD: 29 puntos, respondibles sin morir en el intento.
//
// Los 29 puntos siguen estando —no se quita ninguno, vienen del procedimiento y la autoridad los
// cita por su numero—. Lo que cambia es COMO se responden. Antes era una lista plana de 29 filas
// iguales dentro de un scroll: 28 decisiones una por una, sin forma de avanzar en bloque y sin
// sensacion de progreso. En un caso real la mitad son "No aplica" y se sabe de un vistazo.
//
// Cuatro mecanismos, en orden de cuanto ahorran:
//   1. ACCION MASIVA con nota compartida. "Marcar los 14 que faltan como No aplica" + UNA
//      justificacion para el grupo. No es solo mas rapido: 14 observaciones vacias es lo que un
//      auditor mira, y una razon explicita del bloque es mejor expediente.
//   2. BLOQUES tematicos (A-F) que se pliegan al completarse. La lista se acorta segun avanzas.
//      La numeracion oficial NO se toca.
//   3. TECLADO: S / N / A responden, Enter salta al siguiente sin responder.
//   4. La OBSERVACION se colapsa salvo que haga falta: si la respuesta es "Si" es un hallazgo y
//      se abre sola; en los puntos descriptivos tambien, porque ahi el dato ES la observacion.
//
// El punto 26 no se responde (es una clausula de costos) y el 23 es continuacion del 22: van
// tratados aparte, no como dos filas mas.
import type { KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronRight, Info, Keyboard, Lock, SkipForward } from 'lucide-react';
import { Button, StatusBadge, Mono, Input, Alert, Modal, Field, Textarea } from './ui';
import { INSTRUCTIVO, BLOQUES_INSTRUCTIVO } from '../data/instructivo';
import type { PuntoDelInstructivo } from '../data/instructivo';

// El 26 es una clausula de costos: no se responde. El resto si.
// El tono del avance: verde solo cuando esta completo, ambar en cuanto hay algo
// hecho, gris si no se ha empezado. Compartido entre la cabecera y cada bloque
// para que no puedan discrepar.
const tonoDeAvance = (completo: boolean, hechos: number): 'success' | 'warning' | 'neutral' => {
  if (completo) return 'success';
  return hechos ? 'warning' : 'neutral';
};

export const RESPONDIBLES = INSTRUCTIVO.filter((p) => p.nota !== 'clausula');

// HU-12-C11: EL 23 NO SE RESPONDE, SE DERIVA DEL 22. El backend lo aplica al guardar
// (`instructivo.py::_derivar_desde`) y RECHAZA una respuesta directa al 23 con 422
// (`test_el_23_no_se_responde_a_mano`). Hasta el 17-sep-2026 la pantalla no replicaba
// la regla: tras «No» en el 22 el 23 seguia «sin responder», ofrecia sus botones, y si
// alguien los tocaba el PATCH entero se caia (Bug 712 de QA). Aqui vive la regla para el
// front: el origen es el punto anterior al de nota «continuacion», igual que en el dominio,
// y el texto es el literal de la HU.
export const PUNTO_DERIVADO = INSTRUCTIVO.find((p) => p.nota === 'continuacion')?.n ?? 23;
export const PUNTO_ORIGEN = PUNTO_DERIVADO - 1;
export const OBSERVACION_DERIVADA = `Derivado del punto ${PUNTO_ORIGEN}: sin análisis de laboratorio no hay resultados`;
/** «No» o «No aplica» en el origen cierran la derivacion; «Sí» (o borrar) la retiran. */
export const cierraLaDerivacion = (valor: string | undefined): boolean => valor === 'No' || valor === 'No aplica';
/**
 * HU-12-C11 completada (18-sep-2026, QA Bug 712): con «Sí» en el 22 hubo laboratorio y
 * alguien informa los resultados en el 23 -se responde a mano, con observacion-. Sin el
 * 22, o con «No»/«No aplica» (derivado), el 23 no se toca. Es la misma regla del dominio.
 */
export const elDerivadoSeRespondeAMano = (valorDelOrigen: string | undefined): boolean => valorDelOrigen === 'Sí';
/** Los que se responden a mano ahora mismo: sin la clausula, y el derivado solo con «Sí» en su origen. Es lo que recorre Enter. */
export const respondiblesAMano = (respuestas: Record<number, RespuestaDelPunto>): PuntoDelInstructivo[] =>
  RESPONDIBLES.filter((p) => p.nota !== 'continuacion' || elDerivadoSeRespondeAMano(respuestas[PUNTO_ORIGEN]?.valor));

// En los puntos descriptivos "Si/No" no dice nada: lo que vale es lo que se describe.
const OPCIONES = (p: PuntoDelInstructivo): string[] => (p.descriptivo
  ? ['Documentado', 'No se pudo obtener', 'No aplica']
  : ['Sí', 'No', 'No aplica']);

// Una fila. A nivel de modulo: definirla dentro del componente le da identidad nueva en cada
// render y React remonta el subarbol, que es como se perdia el foco al escribir.
/**
 * Lo respondido para un punto: la opcion elegida y la observacion. `protegida` = la
 * observacion guardada es dato L2 y el servidor la mando enmascarada (HU-33): no se
 * muestra ni se reenvia; se ofrece escribir una nueva.
 */
export type RespuestaDelPunto = {
  valor?: string; obs?: string; protegida?: boolean;
  /** HU-12-C11: la puso la regla del 22 -> 23, no una persona. Ni se edita ni se envia. */
  derivada?: boolean;
};

const Punto = ({ p, r, onResponder, onFoco, refFila, hijo, origenEnSi }: {
  p: PuntoDelInstructivo;
  r: RespuestaDelPunto;
  onResponder: (n: number, campo: 'valor' | 'obs', valor: string) => void;
  onFoco: (n: number) => void;
  refFila: (el: HTMLDivElement | null) => void;
  /** El 23 es continuacion del 22: se pinta indentado y sin numero propio. */
  hijo?: boolean;
  /** Solo para el hijo: el 22 esta en «Sí», asi que el 23 se responde a mano (resultados). */
  origenEnSi?: boolean;
}) => {
  // El hijo se comporta como un punto normal cuando su origen esta en «Sí».
  const bloqueado = Boolean(hijo) && !origenEnSi;
  const [verTexto, setVerTexto] = useState(false);
  const opciones = OPCIONES(p);
  // La nota se abre sola cuando aporta: hallazgo ("Sí") o punto que pide describir.
  const notaAbierta = !!r.obs || r.valor === 'Sí' || (p.descriptivo && !!r.valor);
  const [nota, setNota] = useState(false);
  // Con la observacion PROTEGIDA (L2) y nada escrito encima, en vez del campo va la
  // insignia: mostrar un input vacio invitaria a «corregir» un dato que si existe, y
  // reenviarlo vacio lo borraria. «+ Nota» sigue permitiendo escribir una nueva.
  // `r.valor` de guarda (Bug 892): borrado el punto no hay respuesta a la que colgar la
  // insignia, y dejarla decia que hay un dato protegido donde ya no queda ninguno.
  const protegidaSinTexto = Boolean(r.protegida) && Boolean(r.valor) && !r.obs && !nota;
  // El derivado muestra su observacion junto a la insignia, nunca como campo editable.
  const mostrarNota = (notaAbierta || nota) && !protegidaSinTexto && !bloqueado;

  // D6 (HU-12-C13): al responder «Sí» —con el raton o con la S— la observacion que se
  // abre recibe el foco, porque lo siguiente que se hace es escribir que se hallo. Solo
  // en ESA interaccion: no al cargar, no al marcar en bloque, no al deshacer.
  const [enfocarNota, setEnfocarNota] = useState(false);
  const campoDeNota = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!enfocarNota) return;
    if (mostrarNota) campoDeNota.current?.focus({ preventScroll: true });
    setEnfocarNota(false);
  }, [enfocarNota, mostrarNota]);

  const responderValor = (op: string) => {
    onResponder(p.n, 'valor', op);
    const abreLaNota = op === 'Sí' || (p.descriptivo && op === opciones[0]);
    if (abreLaNota && !r.protegida) setEnfocarNota(true);
  };

  const teclas = (e: KeyboardEvent<HTMLElement>) => {
    const k = e.key.toLowerCase();
    const mapa: Record<string, string> = p.descriptivo
      ? { d: opciones[0], o: opciones[1], a: opciones[2] }
      : { s: 'Sí', n: 'No', a: 'No aplica' };
    if (mapa[k]) { e.preventDefault(); responderValor(mapa[k]); return; }
    if (e.key === 'Enter') { e.preventDefault(); onFoco(p.n); }
  };

  // EL FOCO Y LOS ATAJOS VIVEN EN EL `radiogroup`, NO EN LA FILA (T-45, S6845/S6848).
  //
  // Antes la fila era un `<div tabIndex={0}>` con los atajos colgados: un contenedor
  // sin rol que se comporta como control, que es exactamente lo que esas dos reglas
  // marcan — y con razon, porque el lector de pantalla anunciaba un texto suelto y no
  // un mando que hace algo.
  //
  // La nota que habia aqui ya identificaba el arreglo correcto —"que el propio
  // `radiogroup` de dentro asuma el teclado"— y se aplica ahora: el grupo de opciones
  // YA tiene `role="radiogroup"` y sus tres botones `tabIndex={-1}`, que es la mitad
  // del patron ARIA de radios; faltaba la otra mitad, que el contenedor sea lo
  // enfocable. Con eso el tabulador para UNA vez por punto (igual que antes), las
  // letras siguen respondiendo, Enter sigue saltando al siguiente pendiente y el
  // lector anuncia "grupo de opciones, Punto N".
  //
  // `<button>` no era opcion: la fila contiene botones y un campo de texto, y un
  // boton no puede anidar controles.
  //
  // El resalte pasa a `focus-within` para que se siga iluminando la FILA ENTERA
  // cuando el foco entra en ella, que es lo que orientaba la vista.
  // AQUI NO VA NINGUN MANEJADOR. Habia un `onFocus={() => onFoco(p.n, true)}` que no
  // hacia nada: el unico consumidor de `onFoco` ignora la llamada cuando llega con ese
  // segundo argumento (`if (!soloFoco) siguiente(n)`), asi que era una notificacion que
  // moria en el aire. El resalte de la fila al entrar el foco lo pinta `focus-within`,
  // que es CSS y no necesita manejador. De paso deja de ser un `div` inerte con un
  // manejador de interaccion colgado (javascript:S6848).
  return (
    <div
      className={'px-3 py-2.5 focus-within:bg-marca-bruma/25 '
        + (hijo ? 'pl-9 border-l-2 border-hairline ml-4 ' : '')}>
      <div className="flex items-start gap-2.5">
        <Mono className={'text-meta shrink-0 pt-1 ' + (r.valor ? 'text-marca-texto font-semibold' : 'text-ink-soft')}>
          {String(p.n).padStart(2, '0')}
        </Mono>
        <div className="min-w-0 flex-1">
          <p className="flex items-start gap-1.5 text-body text-ink">
            <span className="min-w-0">
              {hijo && <span className="text-ink-soft">…</span>} {p.corto}
              {hijo && <span className="text-meta text-ink-soft"> · continúa el punto 22</span>}
            </span>
            {/* El texto normativo completo no puede vivir en un `title`: invisible con teclado
                y en tactil, y poco fiable con lector de pantalla. */}
            <button type="button" onClick={() => setVerTexto(!verTexto)} aria-expanded={verTexto}
              aria-label={`Ver el texto completo del punto ${p.n}`}
              // HU-51: 24 × 24 px (antes 21, WCAG 2.5.8) y contorno de control, como todo boton de icono.
              className="shrink-0 w-6 h-6 -my-1 grid place-items-center rounded-md border border-control-borde hover:border-control-borde-fuerte text-ink-soft hover:text-marca-texto hover:bg-canvas transition-colors">
              <Info size={13} />
            </button>
          </p>
          {verTexto && (
            <p className="mt-1.5 pl-2.5 border-l-2 border-hairline text-meta text-ink-soft leading-relaxed">
              {p.texto}
            </p>
          )}

          {/* HU-12-C11: el derivado no tiene botones. Con la derivacion cerrada muestra lo
              que la regla puso (y que el servidor confirmara al guardar); sin ella, dice de
              donde sale. Antes ofrecia Si/No/No aplica y el servidor rechazaba la respuesta. */}
          {bloqueado && (
            // `<output>` (rol `status` implicito, aria-live polite): la derivacion ocurre sola al responder
            // el 22, y asi el lector de pantalla la anuncia. Un `aria-label` en un div sin rol no se anuncia.
            <output className="mt-1.5 flex flex-wrap items-center gap-2" aria-label={`Punto ${p.n}: ${p.corto}`}>
              {r.valor
                ? <>
                    {/* Derivado por la regla, o despachado por «marcar en bloque» (que si lo admite). */}
                    <StatusBadge tone="info" icon={Check}>{r.valor}{r.derivada ? ` · derivado del punto ${PUNTO_ORIGEN}` : ''}</StatusBadge>
                    <span className="text-meta text-ink-soft">{r.obs}</span>
                  </>
                : <span className="text-meta text-ink-soft">
                    Depende del punto {PUNTO_ORIGEN}: respóndelo primero. Si allí es «No» o «No aplica», este queda en «No aplica».
                  </span>}
            </output>
          )}
          {/* `tabIndex={0}` + los radios en `-1`: el patron ARIA de un grupo de radios,
              donde el tabulador entra UNA vez al grupo y dentro se mueve con teclado.
              Es tambien el elemento al que `siguiente()` lleva el foco. */}
          {!bloqueado && <div ref={refFila} role="radiogroup" tabIndex={0} onKeyDown={teclas}
            aria-label={`Punto ${p.n}: ${p.corto}`}
            className="flex flex-wrap items-center gap-1.5 mt-1.5 rounded-md">
            {opciones.map((op) => {
              const sel = r.valor === op;
              return (
                <button key={op} type="button" role="radio" aria-checked={sel} tabIndex={-1}
                  onClick={() => responderValor(op)}
                  className={'px-3 py-1.5 rounded-full border text-meta transition-all min-h-8 ' + (sel
                    ? 'bg-marca-bruma/50 border-marca text-marca-texto font-semibold'
                    : 'bg-white border-control-borde text-ink-soft hover:border-control-borde-fuerte')}>
                  {op}
                </button>
              );
            })}
            {r.valor && !mostrarNota && (
              <button type="button" onClick={() => setNota(true)}
                className="px-2 py-1.5 text-meta text-marca-texto underline underline-offset-2 min-h-8">+ Nota</button>
            )}
            {/* BORRAR ES UN RETIRO, NO UN «SIN RESPONDER» CUALQUIERA (Bug 892). Vaciar el
                valor es la señal que el guardado lee: si el punto YA estaba guardado, viaja
                en `puntos_retirados` y el servidor lo borra; si solo se respondió en esta
                sesión, no viaja nada porque no hay nada que retirar. El padre se encarga de
                que la observación se vaya con la respuesta (`responder` en
                `EvaluacionDelCaso.tsx`): media respuesta borrada no es un estado válido.
                El nombre lleva el número porque hay uno por punto: «Borrar» a secas deja al
                lector de pantalla —y a las pruebas— con 28 botones indistinguibles. */}
            {r.valor && (
              <button type="button" onClick={() => onResponder(p.n, 'valor', '')}
                aria-label={`Borrar la respuesta del punto ${p.n}`}
                className="px-2 py-1.5 text-meta text-ink-soft hover:text-rose-600 underline underline-offset-2 min-h-8">Borrar</button>
            )}
          </div>}

          {protegidaSinTexto && (
            <div className="mt-1.5">
              <StatusBadge tone="danger" icon={Lock}>Observación protegida (L2)</StatusBadge>
            </div>
          )}
          {mostrarNota && (
            // Enter DENTRO de la observacion salta al siguiente pendiente igual que desde
            // el grupo (C12): tras responder «Si» el foco ya esta aqui, y sin esto el
            // teclado se quedaba atrapado en el campo.
            <Input ref={campoDeNota} value={r.obs || ''} onChange={(e) => onResponder(p.n, 'obs', e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onFoco(p.n); } }}
              aria-label={`Observación del punto ${p.n}`}
              placeholder={p.descriptivo ? 'Descríbelo aquí: es lo que entra al informe' : 'Qué se determinó'}
              className="!py-1.5 !text-meta w-full mt-1.5" />
          )}
        </div>
      </div>
    </div>
  );
};

export default function Instructivo({ respuestas, onResponder, onResponderVarios, onDeshacerBloque, ocupado = false }: {
  respuestas: Record<number, RespuestaDelPunto>;
  onResponder: (n: number, campo: 'valor' | 'obs', valor: string) => void;
  /**
   * El marcado en bloque. Si devuelve una promesa, el modal espera con «Guardando…» y la
   * barra de «Deshacer» aparece SOLO cuando el servidor confirmó (`true`): pintarla antes
   * ofrecía deshacer algo que aún no existía —y contra Azure el POST tarda segundos—.
   */
  onResponderVarios: (puntos: number[], valor: string, obs: string) => void | boolean | Promise<boolean | void>;
  /**
   * HU-12-C9. Si se pasa, «Deshacer» del marcado en bloque se delega ENTERO (los puntos y
   * la razón del grupo) en vez de revertir punto por punto: el servidor es quien agrupó
   * por la razón y quien la retira de todos a la vez.
   */
  onDeshacerBloque?: (puntos: number[], razon: string) => void;
  /** Hay una peticion en vuelo: «Deshacer» y el marcado en bloque se deshabilitan. */
  ocupado?: boolean;
}) {
  /** La accion masiva en curso: que puntos abarca y de que bloque salio. */
  /** `bloque` es null cuando la accion abarca TODOS los pendientes, no los de un bloque. */
  type AccionMasiva = { puntos: number[]; bloque: string | null };
  const [masiva, setMasiva] = useState<AccionMasiva | null>(null);
  const [notaMasiva, setNotaMasiva] = useState('');
  const [ultimoDeshacer, setUltimoDeshacer] = useState<{ previo: ({ n: number } & RespuestaDelPunto)[]; cuantos: number; razon: string } | null>(null);
  const filas = useRef<Record<number, HTMLDivElement | null>>({});
  const cajasDeBloque = useRef<Record<string, HTMLDetailsElement | null>>({});

  const r = useCallback((n: number): RespuestaDelPunto => respuestas[n] || {}, [respuestas]);
  const respondidos = RESPONDIBLES.filter((p) => r(p.n).valor).length;
  const pendientes = RESPONDIBLES.filter((p) => !r(p.n).valor);

  const bloques = useMemo(() => BLOQUES_INSTRUCTIVO.map((b) => {
    const puntos = RESPONDIBLES.filter((p) => p.bloque === b.id);
    const hechos = puntos.filter((p) => r(p.n).valor).length;
    return { ...b, puntos, hechos, completo: hechos === puntos.length };
  }), [r]);

  // LOS BLOQUES SE PLIEGAN AL COMPLETARSE, SALVO QUE EL FOCO ESTE DENTRO (14-sep-2026).
  //
  // `open` no va controlado por React: si lo fuera, cada render lo volveria a fijar y
  // el usuario no podria abrir un bloque completo para revisarlo. Se fija UNA vez al
  // montar (abierto si esta incompleto) y despues se cambia a mano solo cuando el bloque
  // CAMBIA de estado: al completarse se pliega —pero no si el foco vive dentro, porque
  // plegar la fila que se esta editando se lo lleva a la nada y hay que volver a
  // buscarla—; al dejar de estar completo se vuelve a abrir. Marcar en bloque cierra el
  // modal antes, asi que el foco esta fuera y el bloque se pliega, como pide HU-12-C10.
  const [abiertoAlMontar] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(bloques.map((b) => [b.id, !b.completo])));
  const completoAntes = useRef<Record<string, boolean>>(Object.fromEntries(bloques.map((b) => [b.id, b.completo])));
  useEffect(() => {
    for (const b of bloques) {
      const antes = completoAntes.current[b.id];
      const caja = cajasDeBloque.current[b.id];
      if (antes !== b.completo && caja) {
        if (!b.completo) caja.open = true;
        else if (!caja.contains(document.activeElement)) caja.open = false;
      }
      completoAntes.current[b.id] = b.completo;
    }
  }, [bloques]);

  // Salta al siguiente punto sin responder, empezando por el que sigue al actual.
  const siguiente = (desde: number) => {
    // Solo lo respondible ahora mismo (HU-12-C11): el derivado entra cuando el 22 esta en «Sí».
    const orden = respondiblesAMano(respuestas).map((p) => p.n);
    const i = orden.indexOf(desde);
    const busca = [...orden.slice(i + 1), ...orden.slice(0, i + 1)];
    const n = busca.find((x) => !r(x).valor);
    if (n && filas.current[n]) filas.current[n].focus();
  };

  const [aplicando, setAplicando] = useState(false);
  const aplicarMasiva = async () => {
    // El modal solo se abre con una accion en curso, pero decirlo explicito evita que
    // un cambio futuro lo llame sin ella y falle en tiempo de ejecucion.
    if (!masiva) return;
    const previo = masiva.puntos.map((n) => ({ n, ...r(n) }));
    const { puntos } = masiva;
    const razon = notaMasiva;
    setAplicando(true);
    let confirmado: boolean | void;
    try {
      confirmado = await onResponderVarios(puntos, 'No aplica', razon);
    } finally {
      setAplicando(false);
    }
    setMasiva(null);
    setNotaMasiva('');
    if (confirmado !== false) setUltimoDeshacer({ previo, cuantos: puntos.length, razon });
  };

  return (
    <div data-instructivo className="border border-hairline rounded-xl">
      {/* Cabecera y barra de trabajo FIJAS arriba: el instructivo ya no vive en un scroll
          propio de 26 rem —era un scroll dentro del scroll de la pagina y se perdia la
          barra al bajar—, asi que el avance y la accion masiva acompanan al lector. */}
      <div className="sticky top-0 z-10 rounded-t-xl overflow-hidden shadow-[0_1px_0_var(--color-hairline)]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-2.5 bg-canvas">
        <span className="text-meta font-semibold text-ink">
          Investigación de causalidad · {RESPONDIBLES.length} puntos por responder
          <span className="font-normal text-ink-soft"> · 1 cláusula informativa (26)</span>
        </span>
        <StatusBadge tone={tonoDeAvance(respondidos === RESPONDIBLES.length, respondidos)}>
          {respondidos} de {RESPONDIBLES.length}
        </StatusBadge>
      </div>

      {/* Barra de trabajo: lo que convierte 28 decisiones en unas pocas. */}
      {pendientes.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-3.5 py-2.5 border-b border-hairline bg-white">
          <Button data-testid="evaluacion-investigacion-marcar-en-bloque" variant="secondary" size="sm" icon={Check}
            onClick={() => { setMasiva({ puntos: pendientes.map((p) => p.n), bloque: null }); setNotaMasiva(''); }}>
            Marcar los {pendientes.length} que faltan como «No aplica»
          </Button>
          <Button variant="ghost" size="sm" icon={SkipForward} onClick={() => siguiente(-1)}>
            Siguiente sin responder
          </Button>
          <span className="flex items-center gap-1.5 text-meta text-ink-soft ml-auto">
            <Keyboard size={12} /> S sí · N no · A no aplica · ↵ siguiente
          </span>
        </div>
      )}
      </div>

      {ultimoDeshacer && (
        <div className="flex items-center justify-between gap-3 px-3.5 py-2 bg-marca-bruma/30 border-b border-hairline">
          <span className="text-meta text-ink">{ultimoDeshacer.cuantos} puntos marcados como «No aplica».</span>
          <button type="button" data-testid="evaluacion-investigacion-deshacer" className="text-meta text-marca-texto font-semibold hover:underline disabled:opacity-50"
            disabled={ocupado}
            onClick={() => {
              if (onDeshacerBloque && ultimoDeshacer) {
                onDeshacerBloque(ultimoDeshacer.previo.map((x) => x.n), ultimoDeshacer.razon);
              } else {
                ultimoDeshacer?.previo.forEach((x) => { onResponder(x.n, 'valor', x.valor || ''); onResponder(x.n, 'obs', x.obs || ''); });
              }
              setUltimoDeshacer(null);
            }}>Deshacer</button>
        </div>
      )}

      {/* WCAG 2.4.11: lo que recibe el foco bajo la barra fija se desplaza por debajo de ella
          (`scroll-mt-28`, la altura de la cabecera y la barra de trabajo); sin esto, al tabular
          hacia el bloque A su cabecera quedaba tapada por la barra. */}
      <div className="rounded-b-xl overflow-hidden [&_:is(summary,button,input,textarea)]:scroll-mt-28">
        {bloques.map((b) => {
          const faltan = b.puntos.filter((p) => !r(p.n).valor);
          return (
            <details key={b.id} open={abiertoAlMontar[b.id]} ref={(el) => { cajasDeBloque.current[b.id] = el; }}
              className="border-b border-hairline last:border-0">
              <summary className="flex flex-wrap items-center gap-2 px-3.5 py-2 bg-white text-meta cursor-pointer hover:bg-canvas/60">
                <ChevronRight size={13} className="text-ink-soft shrink-0" />
                <Mono className="text-meta text-ink-soft">{b.id}</Mono>
                <span className="font-semibold text-ink">{b.titulo}</span>
                <StatusBadge tone={tonoDeAvance(b.completo, b.hechos)}>
                  {b.hechos} de {b.puntos.length}
                </StatusBadge>
                {faltan.length > 0 && faltan.length < b.puntos.length && (
                  <button type="button" className="ml-auto text-meta text-marca-texto hover:underline"
                    onClick={(e) => { e.preventDefault(); setMasiva({ puntos: faltan.map((p) => p.n), bloque: b.titulo }); setNotaMasiva(''); }}>
                    Marcar los {faltan.length} restantes como «No aplica»
                  </button>
                )}
              </summary>
              <div className="divide-y divide-hairline bg-white">
                {b.puntos.map((p) => (
                  <Punto key={p.n} p={p} r={r(p.n)} onResponder={onResponder}
                    hijo={p.nota === 'continuacion'}
                    origenEnSi={elDerivadoSeRespondeAMano(respuestas[PUNTO_ORIGEN]?.valor)}
                    refFila={(el) => { filas.current[p.n] = el; }}
                    onFoco={(n) => siguiente(n)} />
                ))}
                {/* El 26 vive al pie de su bloque, fuera de la lista de respuesta. */}
                {b.id === 'E' && (
                  <div className="p-3">
                    <Alert tone="info" title="Punto 26 · No se responde">
                      No es un aspecto a investigar: es la condición de costos del procedimiento. El análisis lo
                      paga el titular del registro y lo realiza un laboratorio oficial de la autoridad.
                    </Alert>
                  </div>
                )}
              </div>
            </details>
          );
        })}
      </div>

      {/* La accion masiva pide UNA razon para el grupo: 14 observaciones vacias es justo lo que
          un auditor mira. Una justificacion explicita del bloque es mejor expediente. */}
      <Modal open={!!masiva} onClose={() => { if (!aplicando) setMasiva(null); }}
        eyebrow="Comité · Investigación de causalidad" title={`¿Por qué no aplican estos ${masiva?.puntos.length || 0} puntos?`}
        icon={Check} tone="info"
        footer={<>
          <Button variant="secondary" onClick={() => setMasiva(null)} disabled={aplicando}>Cancelar</Button>
          <Button variant="primary" disabled={!notaMasiva.trim() || ocupado || aplicando} onClick={() => { aplicarMasiva(); }} aria-busy={aplicando}>
            {aplicando ? 'Guardando…' : `Marcar los ${masiva?.puntos.length || 0}`}
          </Button>
        </>}>
        <p className="text-meta text-ink">
          Se guardará la misma nota en {masiva?.puntos.length === 1 ? 'el punto' : `los ${masiva?.puntos.length} puntos`}
          {masiva?.bloque ? <> del bloque <b>{masiva.bloque}</b></> : ' que aún no respondiste'}. Después puedes
          editar cualquiera por separado.
        </p>
        <Field label="Razón" required hint="Entra al informe FAR-03 como sustento de por qué no se investigaron">
          <Textarea rows={3} value={notaMasiva} onChange={(e) => setNotaMasiva(e.target.value)}
            placeholder="ej. No hubo necropsia ni envío de muestras a laboratorio; los animales se recuperaron." />
        </Field>
        <p className="text-meta text-ink-soft">
          Puntos: <Mono>{(masiva?.puntos || []).map((n) => String(n).padStart(2, '0')).join(' · ')}</Mono>
        </p>
      </Modal>
    </div>
  );
}
